// @ts-strict-ignore
import { CopiedRangeOnLineArticleInformation } from '@project/components/article/model/copied-range-on-line-article-information';
import { CopiedArticleInformation, CopiedLabelStripArticleInformation, CopiedTextureInformation } from '@project/components/article/model/copied-article-information';
import { ModelConversionService } from '@shared/convert/model-conversion.service';
import { Euler, Vector3 } from 'three';
import { OverlapInfo } from '@3d-scene/model/overlap-info';
import { StorageDataService } from '@shared/storage-data';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { Injectable } from '@angular/core';
import { Log } from '@shared/debug/log';
import { StringUtils } from '@shared/util/string-utils';

@Injectable({ providedIn: 'root' })
export class ClipboardManager {
  private readonly _clipboardDataKey = 'ClipboardData';

  constructor(private modelConversionService: ModelConversionService, private _storageDataService: StorageDataService) {}

  public saveRangeOnLineInfoToClipboard(rangeOnLineInfo: CopiedRangeOnLineArticleInformation): void {
    const data: string = JSON.stringify(rangeOnLineInfo);
    const compressedString = StringUtils.compressString(data);
    this._storageDataService.write(this._clipboardDataKey, compressedString);
  }

  public getRangeOnLineInfoFromClipboard(): CopiedRangeOnLineArticleInformation | null {
    const storedData: string = this._storageDataService.read(this._clipboardDataKey);
    if (!storedData) {
      return null;
    }
    let convertedData: CopiedRangeOnLineArticleInformation;
    try {
      const decompressedString = StringUtils.decompressString(storedData);
      convertedData = JSON.parse(decompressedString);
    } catch (error) {
      Log.warn("Failed parsing data from the clipboard", error);
      return null;
    }
    const rangeOnLineArticleInformation = new CopiedRangeOnLineArticleInformation();
    const articlesInformations: CopiedArticleInformation[] = convertedData.articlesInformation.map((info) => {
      return this.convertCopiedArticleInformation(info);
    });
    const articlesOnRailInformations: CopiedArticleInformation[] = convertedData.articlesOnRailInformation.map((info) => {
      const exist: CopiedArticleInformation = articlesInformations.find((inf) => inf.articleUsageId === info.articleUsage.id);
      if (exist) {
        return exist;
      } else {
        return this.convertCopiedArticleInformation(info);
      }
    });
    rangeOnLineArticleInformation.projectType = convertedData.projectType;
    rangeOnLineArticleInformation.rangeOnLineArticleType = convertedData.rangeOnLineArticleType;
    rangeOnLineArticleInformation.articlesInformation = articlesInformations;
    rangeOnLineArticleInformation.articlesOnRailInformation = articlesOnRailInformations;
    rangeOnLineArticleInformation.commonArticlesLength = convertedData.commonArticlesLength;
    rangeOnLineArticleInformation.overlapInfo = this.convertOverlapInfo(convertedData.overlapInfo);
    return rangeOnLineArticleInformation;
  }

  private convertCopiedArticleInformation(data: any): CopiedArticleInformation {
    const qualifiedClassName: string = data.qualifiedClassName;
    let newInfo: CopiedArticleInformation;
    if (qualifiedClassName === CopiedLabelStripArticleInformation.QUALIFIED_CLASS_NAME) {
      const labelStripInfo: CopiedLabelStripArticleInformation = new CopiedLabelStripArticleInformation();
      labelStripInfo.textureConnectionInfos = this.copyTextureConnectionInfos(data.textureConnectionInfos);

      newInfo = labelStripInfo;
    } else {
      newInfo = new CopiedArticleInformation();
    }
    this.applyCopiedArticleInformation(newInfo, data);
    return newInfo;
  }

  private applyCopiedArticleInformation(newInfo: CopiedArticleInformation, data: any): void {
    newInfo.isDynamic = data.isDynamic;
    newInfo.articleUsage = this.modelConversionService.convertArticleUsageVO(data.articleUsage);
    newInfo.dynamicArticleInfo = data.dynamicArticleInfo;
    newInfo.dynamicArticleMetaElements = this.modelConversionService.convertMetaElementVOs(data.dynamicArticleMetaElements);
    newInfo.offsetFromCenter = this.copyVector3(data.offsetFromCenter);
    newInfo.rotation = new Euler().copy(data.rotation);
    newInfo.oldArticleUsageId = data.oldArticleUsageId;
  }

  private convertOverlapInfo(data: any): OverlapInfo {
    const result: OverlapInfo = new OverlapInfo(
      data.overlapTypes,
      data.overlapLeft,
      data.overlapRight,
      data.overlapLeftTypes,
      data.overlapRightTypes
    );
    return result;
  }

  private copyTextureConnectionInfos(source: any[]) {
    if (!source) {
      return [];
    }

    return source.map((data) => this.copyTextureConnectionInfo(data));
  }

  private copyTextureConnectionInfo(data: any): CopiedTextureInformation {
    const textureInfo: CopiedTextureInformation = new CopiedTextureInformation();
    textureInfo.metaElementName = data.metaElementName;
    textureInfo.distanceToPreviousConnection = data.distanceToPreviousConnection;
    textureInfo.localRectangle = this.copyRectangle3D(data.localRectangle);
    return textureInfo;
  }

  private copyVector3(data: any): Vector3 {
    return new Vector3().copy(data);
  }

  private copyRectangle3D(data): Rectangle3D {
    return Rectangle3D.fromArray([
      this.copyVector3(data.point1),
      this.copyVector3(data.point2),
      this.copyVector3(data.point3),
      this.copyVector3(data.point4),
    ]);
  }
}
